// @mui material components
import React from "react";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

// Senthil Solar React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";

// Senthil Solar React examples
import DefaultNavbar from "examples/Navbars/DefaultNavbar";
import DefaultFooter from "examples/Footers/DefaultFooter";

// Icons
import AgricultureIcon from "@mui/icons-material/Agriculture";
import MoneyOffIcon from "@mui/icons-material/MoneyOff";
import SettingsIcon from "@mui/icons-material/Settings";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

// Routes
import routes from "routes";
import footerRoutes from "footer.routes";

// Images - Use an existing image or add 'waterpump_bg.jpg' to src/assets/images/
// For now, reusing 'canva_banner.png' from Awards as a placeholder; replace with a water pump themed bg
import bgImage from "assets/images/canva_banner.png";

// Animation imports
import { useSpring, animated, useTrail } from 'react-spring';
import { useInView } from 'react-intersection-observer';

// Create animated versions of MUI components
const AnimatedGrid = animated(Grid);
const AnimatedMKBox = animated(MKBox);

function WaterPumpService() {
  // For header animation
  const [headerRef, headerInView] = useInView({
    triggerOnce: false,
    threshold: 0.1,
  });
  
  const headerAnimation = useSpring({
    opacity: headerInView ? 1 : 0,
    transform: headerInView ? 'translateY(0)' : 'translateY(-50px)',
    config: { mass: 1, tension: 280, friction: 60 }
  });

  // For why choose section
  const [whyRef, whyInView] = useInView({
    triggerOnce: false,
    threshold: 0.2,
  });

  const whyHeaderAnimation = useSpring({
    opacity: whyInView ? 1 : 0,
    transform: whyInView ? 'translateY(0)' : 'translateY(30px)',
    config: { mass: 1, tension: 280, friction: 60 }
  });

  const whyItems = [
    { icon: <MoneyOffIcon />, title: "Zero Operating Costs", desc: "No electricity bills, no diesel expenses. Free energy from the sun for 25+ years." },
    { icon: <SettingsIcon />, title: "Low Maintenance", desc: "Minimal moving parts, no fuel management, and annual servicing keeps systems running smoothly." },
    { icon: <AgricultureIcon />, title: "Government Subsidy", desc: "Up to 90% subsidy under KUSUM scheme for farmers. We handle all paperwork and approvals." },
    { icon: <CheckCircleIcon />, title: "Reliable Performance", desc: "Works even on cloudy days, intelligent controllers optimize water pumping based on sunlight." }
  ];

  const [whyItemsRef, whyItemsInView] = useInView({
    triggerOnce: false,
    threshold: 0.1,
  });

  const whyTrail = useTrail(whyItems.length, {
    opacity: whyItemsInView ? 1 : 0,
    transform: whyItemsInView ? 'translateY(0) scale(1)' : 'translateY(50px) scale(0.95)',
    config: { mass: 1, tension: 280, friction: 50 },
  });

  // For applications section
  const [appsRef, appsInView] = useInView({
    triggerOnce: false,
    threshold: 0.2,
  });
  
  const appsAnimation = useSpring({
    opacity: appsInView ? 1 : 0,
    transform: appsInView ? 'translateY(0)' : 'translateY(50px)',
    config: { mass: 1, tension: 280, friction: 60 }
  });

  const applications = [
    {
      title: "Agricultural Irrigation",
      description: "Perfect for farms, orchards, and plantations requiring consistent water supply for crops."
    },
    {
      title: "Drinking Water Supply",
      description: "Reliable water pumping for villages, communities, and remote areas without grid access."
    },
    {
      title: "Livestock Watering",
      description: "Ensure continuous water availability for cattle, poultry, and other livestock."
    },
    {
      title: "Horticulture & Nurseries",
      description: "Automated irrigation systems for greenhouses, gardens, and plant nurseries."
    }
  ];

  const [appsItemsRef, appsItemsInView] = useInView({
    triggerOnce: false,
    threshold: 0.1,
  });

  const appsTrail = useTrail(applications.length, {
    opacity: appsItemsInView ? 1 : 0,
    transform: appsItemsInView ? 'translateY(0) scale(1)' : 'translateY(50px) scale(0.95)',
    config: { mass: 1, tension: 280, friction: 50 },
  });

  // For specs section
  const [specsRef, specsInView] = useInView({
    triggerOnce: false,
    threshold: 0.2,
  });
  
  const specsAnimation = useSpring({
    opacity: specsInView ? 1 : 0,
    transform: specsInView ? 'translateX(0)' : 'translateX(-50px)',
    config: { mass: 1, tension: 280, friction: 60 }
  });

  const specifications = [
    "Pump capacity: 0.5 HP to 15 HP",
    "Maximum head: Up to 200 meters",
    "Flow rate: 10,000 to 2,00,000 liters per day",
    "Submersible and surface pump options",
    "BLDC motor technology for efficiency",
    "Controller with dry run protection"
  ];

  const [specsItemsRef, specsItemsInView] = useInView({
    triggerOnce: false,
    threshold: 0.1,
  });

  const specsTrail = useTrail(specifications.length, {
    opacity: specsItemsInView ? 1 : 0,
    transform: specsItemsInView ? 'scale(1)' : 'scale(0.8)',
    config: { mass: 1, tension: 280, friction: 50 },
  });


  return (
    <>
      <DefaultNavbar
        routes={routes}
        transparent
        light
      />
      <MKBox
        minHeight="60vh"
        width="100%"
        sx={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(${bgImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: "grid",
          placeItems: "center",
        }}
      >
        <Container sx={{ mt: 6 }}>
  <AnimatedGrid
    ref={headerRef}
    style={headerAnimation}
    container
    item
    xs={12}
    lg={7}
    justifyContent="center"
    mx="auto"
  >
    {/* First line: Heading */}
    <MKTypography
      variant="h1"
      color="white"
      mt={-6}
      mb={1}
      sx={({ breakpoints, typography: { size } }) => ({
        textAlign: "center",
        [breakpoints.down("sm")]: { fontSize: size["xl"] },
        [breakpoints.between("sm", "md")]: { fontSize: size["2xl"] },
        [breakpoints.up("md")]: { fontSize: size["3xl"] },
      })}
    >
      Solar Water Pumps
    </MKTypography>

    {/* Second line: Subtitle */}
    <MKTypography
      variant="h5"
      color="white"
      textAlign="center"
      sx={({ breakpoints, typography: { size } }) => ({
        opacity: 0.9,
        [breakpoints.down("sm")]: { fontSize: size["md"] },
        [breakpoints.between("sm", "md")]: { fontSize: size["lg"] },
        [breakpoints.up("md")]: { fontSize: size["xl"] },
      })}
    >
      Sustainable Water Solutions for Agriculture & Rural Areas
    </MKTypography>
  </AnimatedGrid>
</Container>
      </MKBox>
      <Card
        sx={{
          p: 2,
          mx: { xs: 2, lg: 3 },
          mt: -8,
          mb: 4,
          backgroundColor: ({ palette: { white }, functions: { rgba } }) => rgba(white.main, 0.8),
          backdropFilter: "saturate(200%) blur(30px)",
          boxShadow: ({ boxShadows: { xxl } }) => xxl,
        }}
      >
        <MKBox component="section" py={6}>
          <Container>
            <Grid container item xs={12} lg={10} mx="auto" mb={4}>
              <MKTypography variant="body1" color="text" textAlign="center">
                Solar water pumps are revolutionizing agriculture and rural water supply across India. 
                Our solar pumping systems eliminate electricity costs, provide reliable water access in 
                remote locations, and are environmentally friendly. With government subsidies covering 
                up to 90% of the cost for farmers, solar water pumps have become the most economical 
                irrigation solution.
              </MKTypography>
            </Grid>
            
            {/* Why Choose Section */}
            <AnimatedGrid 
              ref={whyRef}
              style={whyHeaderAnimation}
              container 
              item 
              xs={12} 
              justifyContent="center" 
              mx="auto" 
              mb={4}
            >
              <MKTypography variant="h4" textAlign="center" mb={3}>
                Why Choose Solar Water Pumps?
              </MKTypography>
            </AnimatedGrid>

            <Grid container spacing={3} ref={whyItemsRef}>
              {whyTrail.map((style, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <AnimatedMKBox 
                    style={style}
                    display="flex" 
                    alignItems="flex-start" 
                    mb={2}
                  >
                    <MKBox color={index === 0 ? "success.main" : index === 1 ? "info.main" : index === 2 ? "warning.main" : "error.main"} mr={2} mt={0.5}>
                      {React.cloneElement(whyItems[index].icon, { fontSize: "large" })}
                    </MKBox>
                    <MKBox>
                      <MKTypography variant="h6" mb={0.5}>
                        {whyItems[index].title}
                      </MKTypography>
                      <MKTypography variant="body2" color="text">
                        {whyItems[index].desc}
                      </MKTypography>
                    </MKBox>
                  </AnimatedMKBox>
                </Grid>
              ))}
            </Grid>

            {/* Applications Section */}
            <AnimatedGrid 
              ref={appsRef}
              style={appsAnimation}
              container 
              item 
              xs={12} 
              md={10} 
              lg={8} 
              justifyContent="center" 
              mx="auto" 
              mt={6}
            >
              <MKTypography variant="h4" textAlign="center" mb={3}>
                Applications
              </MKTypography>
            </AnimatedGrid>

            <Grid container spacing={3} ref={appsItemsRef}>
              {appsTrail.map((style, index) => (
                <Grid item xs={12} sm={6} key={index}>
                  <AnimatedMKBox 
                    style={style}
                    p={3} 
                    borderRadius="lg" 
                    sx={{ 
                      backgroundColor: "rgba(33, 150, 243, 0.05)",
                      height: "100%"
                    }}
                  >
                    <MKTypography variant="h5" mb={1}>
                      {applications[index].title}
                    </MKTypography>
                    <MKTypography variant="body2" color="text">
                      {applications[index].description}
                    </MKTypography>
                  </AnimatedMKBox>
                </Grid>
              ))}
            </Grid>

            {/* Technical Specifications Section */}
            <AnimatedGrid 
              ref={specsRef}
              style={specsAnimation}
              container 
              item 
              xs={12} 
              md={10} 
              lg={8} 
              justifyContent="center" 
              mx="auto" 
              mt={6}
            >
              <MKTypography variant="h4" mb={3}>
                Technical Specifications
              </MKTypography>
            </AnimatedGrid>

            <Grid container spacing={2} ref={specsItemsRef}>
              {specsTrail.map((style, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <animated.div style={style}>
                    <MKBox display="flex" alignItems="center">
                      <CheckCircleIcon sx={{ color: "success.main", mr: 1, fontSize: 20 }} />
                      <MKTypography variant="body2" color="text">
                        {specifications[index]}
                      </MKTypography>
                    </MKBox>
                  </animated.div>
                </Grid>
              ))}
            </Grid>
          </Container>
        </MKBox>
      </Card>
      <MKBox pt={6} px={1} mt={6}>
        <DefaultFooter content={footerRoutes} />
      </MKBox>
    </>
  );
}

export default WaterPumpService;